import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import InsightArticle from "../../components/insights/insight-article";
import {
  archiveInsight,
  createInsight,
  listInsights,
  publishInsight,
  unpublishInsight,
  updateInsight,
} from "../../services/insights-admin-service";
import { publishDraftLive } from "../../services/live-blog-publisher";
import {
  calcSeoCompleteness,
  normalizeInsightSections,
  parseInsightSections,
  slugify,
  stringifyInsightSections,
  toCsvList,
} from "../../components/insights/insight-utils";
import "../../assets/css/insights.css";

const STATUS_OPTIONS = ["published", "draft", "archived"];
const EMPTY = "All";

const blankDraft = () => ({
  title: "",
  slug: "",
  subtitle: "",
  excerpt: "",
  category: "",
  tags: "",
  authorName: "Avani Cimcon Editorial Team",
  authorTitle: "Research and Strategy",
  publishDate: new Date().toISOString().slice(0, 10),
  updatedDate: new Date().toISOString().slice(0, 10),
  readingTime: "5 min read",
  status: "draft",
  featured: false,
  featuredRank: 999,
  heroImage: "/images/hero-bridge.svg",
  heroImageAlt: "",
  metaTitle: "",
  metaDescription: "",
  canonicalUrl: "",
  keywords: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "https://www.avanicimcon.com/images/og-homepage.jpg",
  twitterTitle: "",
  twitterDescription: "",
  relatedServices: "",
  relatedIndustries: "",
  relatedProducts: "",
  relatedPosts: "",
  ctaTitle: "Talk to Avani Cimcon",
  ctaText: "Discuss your modernization roadmap with our team.",
  ctaButtonText: "Contact Us",
  ctaButtonUrl: "/contact-us",
  bodySectionsText: stringifyInsightSections([
    {
      heading: "Why it matters",
      paragraphs: ["Explain why the topic matters to enterprise software leaders."],
      bullets: ["Add a practical bullet or two here."],
    },
    {
      heading: "A practical path",
      paragraphs: ["Describe the modernization or AI pattern in a grounded way."],
    },
  ]),
});

const hydrateDraft = (post) => ({
  title: post.title || "",
  slug: post.slug || "",
  subtitle: post.subtitle || "",
  excerpt: post.excerpt || "",
  category: post.category || "",
  tags: (post.tags || []).join(", "),
  authorName: post.authorName || "",
  authorTitle: post.authorTitle || "",
  publishDate: post.publishDate || "",
  updatedDate: post.updatedDate || "",
  readingTime: post.readingTime || "",
  status: post.status || "draft",
  featured: !!post.featured,
  featuredRank: String(post.featuredRank ?? 999),
  heroImage: post.heroImage || "",
  heroImageAlt: post.heroImageAlt || "",
  metaTitle: post.metaTitle || "",
  metaDescription: post.metaDescription || "",
  canonicalUrl: post.canonicalUrl || "",
  keywords: (post.keywords || []).join(", "),
  ogTitle: post.ogTitle || "",
  ogDescription: post.ogDescription || "",
  ogImage: post.ogImage || "",
  twitterTitle: post.twitterTitle || "",
  twitterDescription: post.twitterDescription || "",
  relatedServices: (post.relatedServices || []).join(", "),
  relatedIndustries: (post.relatedIndustries || []).join(", "),
  relatedProducts: (post.relatedProducts || []).join(", "),
  relatedPosts: (post.relatedPosts || []).join(", "),
  ctaTitle: post.ctaTitle || "",
  ctaText: post.ctaText || "",
  ctaButtonText: post.ctaButtonText || "",
  ctaButtonUrl: post.ctaButtonUrl || "",
  bodySectionsText: stringifyInsightSections(post.bodySections || []),
});

const makePayload = (draft, existing = null) => {
  const parsedSections = normalizeInsightSections(parseInsightSections(draft.bodySectionsText));
  const slugValue = draft.slug ? slugify(draft.slug) : slugify(draft.title);
  return {
    ...(existing || {}),
    title: draft.title.trim(),
    slug: slugValue,
    subtitle: draft.subtitle.trim(),
    excerpt: draft.excerpt.trim(),
    category: draft.category.trim(),
    tags: toCsvList(draft.tags),
    authorName: draft.authorName.trim(),
    authorTitle: draft.authorTitle.trim(),
    publishDate: draft.publishDate,
    updatedDate: draft.updatedDate,
    readingTime: draft.readingTime.trim(),
    status: draft.status,
    featured: !!draft.featured,
    featuredRank: Number(draft.featuredRank || 999),
    heroImage: draft.heroImage.trim(),
    heroImageAlt: draft.heroImageAlt.trim(),
    metaTitle: draft.metaTitle.trim(),
    metaDescription: draft.metaDescription.trim(),
    canonicalUrl: draft.canonicalUrl.trim() || `https://www.avanicimcon.com/blog/${slugValue}`,
    keywords: toCsvList(draft.keywords),
    ogTitle: draft.ogTitle.trim(),
    ogDescription: draft.ogDescription.trim(),
    ogImage: draft.ogImage.trim(),
    twitterTitle: draft.twitterTitle.trim(),
    twitterDescription: draft.twitterDescription.trim(),
    relatedServices: toCsvList(draft.relatedServices),
    relatedIndustries: toCsvList(draft.relatedIndustries),
    relatedProducts: toCsvList(draft.relatedProducts),
    relatedPosts: toCsvList(draft.relatedPosts),
    ctaTitle: draft.ctaTitle.trim(),
    ctaText: draft.ctaText.trim(),
    ctaButtonText: draft.ctaButtonText.trim(),
    ctaButtonUrl: draft.ctaButtonUrl.trim(),
    bodySections: parsedSections,
  };
};

const ControlField = ({ label, children, hint }) => (
  <div className="insights-form-group">
    <label>{label}</label>
    {children}
    {hint ? <small>{hint}</small> : null}
  </div>
);

const InsightsAdmin = () => {
  const [items, setItems] = useState(() => listInsights());
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(blankDraft);
  const [filterStatus, setFilterStatus] = useState(EMPTY);
  const [filterCategory, setFilterCategory] = useState(EMPTY);
  const [filterTag, setFilterTag] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [bodySectionsError, setBodySectionsError] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [liveEmail, setLiveEmail] = useState("");
  const [livePassword, setLivePassword] = useState("");
  const [livePublishState, setLivePublishState] = useState({ status: "", message: "" });
  const [isPublishingLive, setIsPublishingLive] = useState(false);
  const initialSelectionRef = useRef(false);

  const refresh = () => setItems(listInsights());

  useEffect(() => {
    if (!items.length || initialSelectionRef.current) return;
    if (!selectedId) {
      const first = items[0];
      setSelectedId(first.id);
      setDraft(hydrateDraft(first));
      setSlugTouched(false);
    }
    initialSelectionRef.current = true;
  }, [items, selectedId]);

  const categories = useMemo(() => Array.from(new Set(items.map((item) => item.category))).filter(Boolean).sort(), [items]);
  const tags = useMemo(
    () => Array.from(new Set(items.flatMap((item) => item.tags || []))).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesStatus = filterStatus === EMPTY || item.status === filterStatus;
      const matchesCategory = filterCategory === EMPTY || item.category === filterCategory;
      const matchesTag = filterTag === EMPTY || (item.tags || []).includes(filterTag);
      const matchesSearch =
        !q ||
        [item.title, item.subtitle, item.excerpt, item.category, ...(item.tags || [])]
          .join(" ")
          .toLowerCase()
          .includes(q);
      return matchesStatus && matchesCategory && matchesTag && matchesSearch;
    });
  }, [items, filterStatus, filterCategory, filterTag, search]);
  const publishedPreviewItems = useMemo(() => items.filter((item) => item.status === "published"), [items]);

  const selected = items.find((item) => item.id === selectedId) || null;
  const previewPost = useMemo(() => {
    try {
      return makePayload(draft, selected);
    } catch (err) {
      return null;
    }
  }, [draft, selected]);

  const seoScore = previewPost ? calcSeoCompleteness(previewPost) : { percent: 0, missing: [] };
  const bodyCount = parseInsightSections(draft.bodySectionsText).length;

  const handleSelect = (item) => {
    setSelectedId(item.id);
    setDraft(hydrateDraft(item));
    setSlugTouched(false);
    setBodySectionsError("");
  };

  const handleNew = () => {
    const empty = blankDraft();
    setSelectedId(null);
    setDraft(empty);
    setSlugTouched(false);
    setBodySectionsError("");
  };

  const handleDraftChange = (field, value) => {
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === "title" && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSave = () => {
    let parsedSections;
    try {
      parsedSections = normalizeInsightSections(JSON.parse(draft.bodySectionsText || "[]"));
    } catch (err) {
      setBodySectionsError("Body sections JSON is invalid. Please correct the structure before saving.");
      return;
    }

    const payload = makePayload({ ...draft, bodySectionsText: stringifyInsightSections(parsedSections) }, selected);
    if (!payload.title || !payload.slug) {
      setBodySectionsError("Title and slug are required before saving.");
      return;
    }
    setBodySectionsError("");
    if (selected) {
      updateInsight(selected.id, payload);
    } else {
      const created = createInsight(payload);
      setSelectedId(created.id);
      setDraft(hydrateDraft(created));
    }
    refresh();
  };

  const handlePublish = (item) => {
    publishInsight(item.id);
    refresh();
  };

  const handleUnpublish = (item) => {
    unpublishInsight(item.id);
    refresh();
  };

  const handleArchive = (item) => {
    archiveInsight(item.id);
    refresh();
  };

  const handlePublishLive = async () => {
    if (!draft.title) {
      setLivePublishState({ status: "error", message: "Add a title before publishing live." });
      return;
    }

    setIsPublishingLive(true);
    setLivePublishState({ status: "info", message: "Publishing live..." });

    try {
      const result = await publishDraftLive({
        draft,
        existing: selected,
        email: liveEmail.trim(),
        password: livePassword,
      });

      const localPayload = makePayload({ ...draft, status: "published" }, selected);
      localPayload.backendId = result.id;
      localPayload.livePublishedUrl = result.publicUrl;
      if (selected) {
        updateInsight(selected.id, localPayload);
      } else {
        const created = createInsight(localPayload);
        setSelectedId(created.id);
        setDraft(hydrateDraft(created));
      }
      refresh();

      setLivePublishState({
        status: "success",
        message: `Published live: ${result.publicUrl}`,
      });
      setLivePassword("");
    } catch (err) {
      setLivePublishState({
        status: "error",
        message: err.message || "Live publish failed.",
      });
    } finally {
      setIsPublishingLive(false);
    }
  };

  const relatedPreview = previewPost
    ? publishedPreviewItems.filter((item) => item.slug !== previewPost.slug).slice(0, 3)
    : [];

  return (
    <React.Fragment>
      <Helmet defer={false}>
        <title>Admin Insights | Avani Cimcon</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="insights-admin-shell">
        <section className="insights-admin-hero">
          <div className="container">
            <p className="insights-kicker">Admin / Prototype</p>
            <h1>Insights management</h1>
            <p>
              Create, edit, preview and publish blog posts. Draft editing stays browser-local, while the live publish action sends the article directly to the Avani Cimcon backend API.
            </p>
            <div className="insights-admin-note">
              Draft state remains local in the browser. The live publish action authenticates against the backend only when you submit admin credentials.
            </div>
          </div>
        </section>

        <section className="insights-admin-layout">
          <div className="container">
            <div className="insights-admin-card">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: "12px" }}>
                <div>
                  <h2 className="mb-1">Library</h2>
                  <p className="mb-0 insights-help">Search, filter and update the catalog without leaving the page.</p>
                </div>
                <div className="insights-form-actions">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleNew}>
                    New insight
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    Save insight
                  </button>
                  <Link to="/resources/insights" className="btn btn-link">
                    View public listing
                  </Link>
                </div>
              </div>

              <div className="insights-toolbar mb-3" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr" }}>
                <input
                  className="form-control"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search insights"
                />
                <select className="custom-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value={EMPTY}>All statuses</option>
                  {STATUS_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <select className="custom-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value={EMPTY}>All categories</option>
                  {categories.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <select className="custom-select" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
                  <option value={EMPTY}>All tags</option>
                  {tags.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="table-responsive">
                <table className="insights-admin-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Category</th>
                      <th>Publish date</th>
                      <th>SEO</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item) => {
                      const seo = calcSeoCompleteness(item);
                      return (
                        <tr key={item.id} className={selectedId === item.id ? "is-active" : ""}>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-left"
                              onClick={() => handleSelect(item)}
                              style={{ fontWeight: 700 }}
                            >
                              {item.title}
                            </button>
                            <div className="insights-help">{item.slug}</div>
                          </td>
                          <td>
                            <div className={`insights-badge insights-badge--${item.status}`}>{item.status}</div>
                            {item.featured ? <div className="insights-badge insights-badge--featured mt-1">featured</div> : null}
                          </td>
                          <td>{item.category}</td>
                          <td>{item.publishDate}</td>
                          <td>{seo.percent}%</td>
                          <td>
                            <div className="insights-form-actions">
                              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleSelect(item)}>
                                Edit
                              </button>
                              <Link className="btn btn-sm btn-outline-secondary" to={`/blog/${item.slug}`}>
                                Preview
                              </Link>
                              {item.status !== "published" ? (
                                <button type="button" className="btn btn-sm btn-success" onClick={() => handlePublish(item)}>
                                  Publish
                                </button>
                              ) : (
                                <button type="button" className="btn btn-sm btn-warning" onClick={() => handleUnpublish(item)}>
                                  Unpublish
                                </button>
                              )}
                              <button type="button" className="btn btn-sm btn-dark" onClick={() => handleArchive(item)}>
                                Archive
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="insights-admin-columns">
              <div className="insights-admin-card">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h2 className="mb-1">{selected ? "Edit insight" : "Create insight"}</h2>
                    <p className="mb-0 insights-help">All fields are editable. Body sections use JSON for structured content in this static prototype.</p>
                  </div>
                  <div className="text-right">
                    <div className={`insights-badge insights-badge--${draft.status || "draft"}`}>{draft.status || "draft"}</div>
                    {draft.featured ? <div className="insights-badge insights-badge--featured mt-1">featured</div> : null}
                  </div>
                </div>

                <div className="insights-form-grid">
                  <ControlField label="Title">
                    <input className="form-control" value={draft.title} onChange={(e) => handleDraftChange("title", e.target.value)} />
                  </ControlField>
                  <ControlField label="Slug">
                    <input
                      className="form-control"
                      value={draft.slug}
                      onChange={(e) => {
                        setSlugTouched(true);
                        handleDraftChange("slug", e.target.value);
                      }}
                    />
                  </ControlField>
                  <ControlField label="Subtitle">
                    <input className="form-control" value={draft.subtitle} onChange={(e) => handleDraftChange("subtitle", e.target.value)} />
                  </ControlField>
                  <ControlField label="Excerpt">
                    <textarea className="form-control" value={draft.excerpt} onChange={(e) => handleDraftChange("excerpt", e.target.value)} />
                  </ControlField>
                  <ControlField label="Category">
                    <input className="form-control" value={draft.category} onChange={(e) => handleDraftChange("category", e.target.value)} />
                  </ControlField>
                  <ControlField label="Tags" hint="Comma separated">
                    <input className="form-control" value={draft.tags} onChange={(e) => handleDraftChange("tags", e.target.value)} />
                  </ControlField>
                  <ControlField label="Author name">
                    <input className="form-control" value={draft.authorName} onChange={(e) => handleDraftChange("authorName", e.target.value)} />
                  </ControlField>
                  <ControlField label="Author title">
                    <input className="form-control" value={draft.authorTitle} onChange={(e) => handleDraftChange("authorTitle", e.target.value)} />
                  </ControlField>
                  <ControlField label="Publish date">
                    <input type="date" className="form-control" value={draft.publishDate} onChange={(e) => handleDraftChange("publishDate", e.target.value)} />
                  </ControlField>
                  <ControlField label="Updated date">
                    <input type="date" className="form-control" value={draft.updatedDate} onChange={(e) => handleDraftChange("updatedDate", e.target.value)} />
                  </ControlField>
                  <ControlField label="Reading time">
                    <input className="form-control" value={draft.readingTime} onChange={(e) => handleDraftChange("readingTime", e.target.value)} />
                  </ControlField>
                  <ControlField label="Status">
                    <select className="custom-select" value={draft.status} onChange={(e) => handleDraftChange("status", e.target.value)}>
                      {STATUS_OPTIONS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </ControlField>
                  <ControlField label="Featured">
                    <input type="checkbox" checked={draft.featured} onChange={(e) => handleDraftChange("featured", e.target.checked)} />
                  </ControlField>
                  <ControlField label="Featured rank">
                    <input type="number" className="form-control" value={draft.featuredRank} onChange={(e) => handleDraftChange("featuredRank", e.target.value)} />
                  </ControlField>
                  <ControlField label="Hero image URL">
                    <input className="form-control" value={draft.heroImage} onChange={(e) => handleDraftChange("heroImage", e.target.value)} />
                  </ControlField>
                  <ControlField label="Hero image alt text">
                    <input className="form-control" value={draft.heroImageAlt} onChange={(e) => handleDraftChange("heroImageAlt", e.target.value)} />
                  </ControlField>
                  <ControlField label="Meta title">
                    <input className="form-control" value={draft.metaTitle} onChange={(e) => handleDraftChange("metaTitle", e.target.value)} />
                    <small>{draft.metaTitle.length} characters</small>
                  </ControlField>
                  <ControlField label="Meta description">
                    <textarea className="form-control" value={draft.metaDescription} onChange={(e) => handleDraftChange("metaDescription", e.target.value)} />
                    <small>{draft.metaDescription.length} characters</small>
                  </ControlField>
                  <ControlField label="Canonical URL">
                    <input className="form-control" value={draft.canonicalUrl} onChange={(e) => handleDraftChange("canonicalUrl", e.target.value)} />
                  </ControlField>
                  <ControlField label="Keywords" hint="Comma separated">
                    <input className="form-control" value={draft.keywords} onChange={(e) => handleDraftChange("keywords", e.target.value)} />
                  </ControlField>
                  <ControlField label="OG title">
                    <input className="form-control" value={draft.ogTitle} onChange={(e) => handleDraftChange("ogTitle", e.target.value)} />
                  </ControlField>
                  <ControlField label="OG description">
                    <textarea className="form-control" value={draft.ogDescription} onChange={(e) => handleDraftChange("ogDescription", e.target.value)} />
                  </ControlField>
                  <ControlField label="OG image">
                    <input className="form-control" value={draft.ogImage} onChange={(e) => handleDraftChange("ogImage", e.target.value)} />
                  </ControlField>
                  <ControlField label="Twitter title">
                    <input className="form-control" value={draft.twitterTitle} onChange={(e) => handleDraftChange("twitterTitle", e.target.value)} />
                  </ControlField>
                  <ControlField label="Twitter description">
                    <textarea className="form-control" value={draft.twitterDescription} onChange={(e) => handleDraftChange("twitterDescription", e.target.value)} />
                  </ControlField>
                  <ControlField label="Related services" hint="Comma separated URLs">
                    <input className="form-control" value={draft.relatedServices} onChange={(e) => handleDraftChange("relatedServices", e.target.value)} />
                  </ControlField>
                  <ControlField label="Related industries" hint="Comma separated URLs">
                    <input className="form-control" value={draft.relatedIndustries} onChange={(e) => handleDraftChange("relatedIndustries", e.target.value)} />
                  </ControlField>
                  <ControlField label="Related products" hint="Comma separated URLs">
                    <input className="form-control" value={draft.relatedProducts} onChange={(e) => handleDraftChange("relatedProducts", e.target.value)} />
                  </ControlField>
                  <ControlField label="Related posts" hint="Comma separated slugs">
                    <input className="form-control" value={draft.relatedPosts} onChange={(e) => handleDraftChange("relatedPosts", e.target.value)} />
                  </ControlField>
                  <ControlField label="CTA title">
                    <input className="form-control" value={draft.ctaTitle} onChange={(e) => handleDraftChange("ctaTitle", e.target.value)} />
                  </ControlField>
                  <ControlField label="CTA text">
                    <textarea className="form-control" value={draft.ctaText} onChange={(e) => handleDraftChange("ctaText", e.target.value)} />
                  </ControlField>
                  <ControlField label="CTA button text">
                    <input className="form-control" value={draft.ctaButtonText} onChange={(e) => handleDraftChange("ctaButtonText", e.target.value)} />
                  </ControlField>
                  <ControlField label="CTA button URL">
                    <input className="form-control" value={draft.ctaButtonUrl} onChange={(e) => handleDraftChange("ctaButtonUrl", e.target.value)} />
                  </ControlField>
                </div>

                <ControlField
                  label="Body sections JSON"
                  hint={`Structured content blocks. Parsed sections: ${bodyCount}. Use [{ "heading": "...", "paragraphs": ["..."], "bullets": ["..."] }]`}
                >
                  <textarea
                    className="form-control"
                    style={{ minHeight: "200px", fontFamily: "Consolas, monospace" }}
                    value={draft.bodySectionsText}
                    onChange={(e) => handleDraftChange("bodySectionsText", e.target.value)}
                  />
                </ControlField>
                {bodySectionsError ? <div className="alert alert-warning">{bodySectionsError}</div> : null}

                <div className="insights-form-actions mt-3">
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    Save changes
                  </button>
                  <button type="button" className="btn btn-success" onClick={handlePublishLive} disabled={isPublishingLive}>
                    {isPublishingLive ? "Publishing..." : "Publish live"}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleNew}>
                    Reset
                  </button>
                  {selected ? (
                    <Link className="btn btn-outline-dark" to={`/blog/${selected.slug}`}>
                      Preview published route
                    </Link>
                  ) : null}
                </div>

                <div className="alert alert-light mt-3 mb-0">
                  <div className="row">
                    <div className="col-md-6">
                      <label className="mb-1">Admin email</label>
                      <input
                        className="form-control"
                        type="email"
                        value={liveEmail}
                        onChange={(e) => setLiveEmail(e.target.value)}
                        placeholder="admin@example.com"
                      />
                    </div>
                    <div className="col-md-6 mt-3 mt-md-0">
                      <label className="mb-1">Admin password</label>
                      <input
                        className="form-control"
                        type="password"
                        value={livePassword}
                        onChange={(e) => setLivePassword(e.target.value)}
                        placeholder="Enter password only when publishing"
                      />
                    </div>
                  </div>
                  <small className="d-block mt-2">
                    These credentials stay in the browser state only and are used once to obtain a backend token.
                  </small>
                  {livePublishState.message ? (
                    <div
                      className={`mt-3 alert ${
                        livePublishState.status === "success"
                          ? "alert-success"
                          : livePublishState.status === "error"
                            ? "alert-danger"
                            : "alert-info"
                      } mb-0`}
                    >
                      {livePublishState.message}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="insights-admin-card">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h2 className="mb-1">SEO and preview</h2>
                    <p className="mb-0 insights-help">The preview uses the same public rendering path as the blog detail page.</p>
                  </div>
                  {previewPost ? (
                    <Link to={`/blog/${previewPost.slug}`} className="btn btn-sm btn-outline-primary">
                      Preview URL
                    </Link>
                  ) : null}
                </div>

                <div className="insights-seo-meter">
                  <strong>{seoScore.percent}%</strong>
                  <div className="insights-seo-meter-bar" aria-hidden="true">
                    <div className="insights-seo-segment" style={{ width: `${seoScore.percent}%` }} />
                  </div>
                </div>
                {seoScore.missing.length ? (
                  <div className="insights-help mb-3">
                    Missing fields: {seoScore.missing.join(", ")}
                  </div>
                ) : (
                  <div className="insights-help mb-3">All recommended SEO fields are present.</div>
                )}

                <div className="insights-preview-panel">
                  {previewPost ? <InsightArticle post={previewPost} relatedInsights={relatedPreview} preview /> : <div className="p-4">No preview available yet.</div>}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
};

export default InsightsAdmin;
